import { Loader2, Printer } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useGetOneExpeditionBo } from "@/api/expedition.api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Modal } from "@/components/ui/customs/modal";
import { DocumentListItem } from "@/components/shared/documentListItem";
import { FileTypeEnum } from "@/constants/enums/fileType.enum";
import { NoteType } from "@/constants/enums/noteType.enum";

type Props = {
  open: boolean;
  expeditionId: string | null;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  isPrinting?: boolean;
};

export const SinglePrintConfirmationModal = ({
  open,
  expeditionId,
  onClose,
  onConfirm,
  isPrinting = false,
}: Props) => {
  const { t } = useTranslation(["boExpeditions", "common"]);
  const { data: expedition, isLoading, isError } = useGetOneExpeditionBo(expeditionId ?? "");

  const packagingPriority = expedition?.expeditionOptions?.packagingPriority ?? false;
  const packagingNote = expedition?.expeditionNotes?.find(
    (note) => note.type === NoteType.PACKAGING_INSTRUCTION,
  );
  const filesToPrint =
    expedition?.expeditionDocuments?.documents?.filter(
      (document) => document.type === FileTypeEnum.EXPEDITION_FILES_TO_PRINT,
    ) ?? [];

  const handleConfirm = async () => {
    try {
      await onConfirm();
      onClose();
    } catch {
      // Keep the modal open so the user can retry or cancel.
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissible={!isPrinting}
      size="md"
      title={t("singlePrintModal.title")}
      description={t("singlePrintModal.description")}
      footer={
        <div className="flex w-full flex-row justify-between gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isPrinting}
            className="flex-1"
          >
            {t("common:actions.cancel")}
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={isPrinting || isLoading || isError || !expedition}
            className="flex-1"
          >
            {isPrinting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Printer className="h-4 w-4" />
            )}
            {t("singlePrintModal.print")}
          </Button>
        </div>
      }
    >
      {isLoading && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-gray-500" />
        </div>
      )}

      {isError && (
        <p className="py-4 text-center text-sm text-red-600">{t("singlePrintModal.loadError")}</p>
      )}

      {expedition && !isLoading && (
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between gap-3 rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
            <p className="text-sm font-medium text-gray-800">
              {t("singlePrintModal.leavingToday")}
            </p>
            <Badge variant={packagingPriority ? "green" : "lightGray"}>
              {packagingPriority ? t("common:boolean.yes") : t("common:boolean.no")}
            </Badge>
          </div>

          <div className="flex flex-col gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
            <p className="text-sm font-medium text-gray-800">
              {t("singlePrintModal.packagingInstructions")}
            </p>
            {packagingNote?.note ? (
              <p className="text-sm whitespace-pre-wrap text-gray-700">{packagingNote.note}</p>
            ) : (
              <p className="text-sm text-gray-500">
                {t("singlePrintModal.noPackagingInstructions")}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
            <p className="text-sm font-medium text-gray-800">
              {t("singlePrintModal.filesToPrint")}
            </p>
            {filesToPrint.length === 0 ? (
              <p className="text-sm text-gray-500">{t("singlePrintModal.noFilesToPrint")}</p>
            ) : (
              <div className="flex flex-col">
                {filesToPrint.map((file) => (
                  <DocumentListItem key={file.id} file={file} showDetailsInfo={false} />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
};
